import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui'
import { useTenant, type Tenant } from '@/contexts/TenantContext'
import { resolveTenantReturnPath } from '@/lib/tenantReturnPath'
import { Building2, Check, ChevronDown } from 'lucide-react'
import { useNavigate, useLocation } from 'react-router-dom'

export function TenantSwitcher({ collapsed = false }: { collapsed?: boolean } = {}) {
  const { currentTenant, availableTenants, setCurrentTenant } = useTenant()
  const navigate = useNavigate()
  const location = useLocation()
  const isOnTenantSelectPage = location.pathname === '/select-tenant'

  const activeTenants = availableTenants.filter(t => t.status === 'Active')

  const handleSwitch = (tenant: Tenant) => {
    const requestedPath = (location.state as { from?: string } | null)?.from
    const returnTo = isOnTenantSelectPage ? resolveTenantReturnPath(requestedPath) : '/'
    setCurrentTenant(tenant)
    navigate(returnTo, { replace: isOnTenantSelectPage })
  }

  if (!currentTenant && activeTenants.length === 0) return null

  const isSingleTenant = activeTenants.length <= 1

  return (
    <div className={collapsed ? 'flex justify-center py-2 mt-4' : 'px-4 pb-2 mt-4'}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {collapsed ? (
            <button
              disabled={isSingleTenant}
              className={`flex h-9 w-9 items-center justify-center rounded-md border transition-colors ${
                isOnTenantSelectPage
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
              aria-label="Switch tenant"
            >
              <Building2 className="h-4 w-4" />
            </button>
          ) : (
            <button
              disabled={isSingleTenant}
              className={`flex w-full items-center justify-between rounded-md border px-3 py-2 text-sm transition-colors ${
                isOnTenantSelectPage
                  ? 'bg-primary text-primary-foreground'
                  : 'hover:bg-muted'
              }`}
            >
              <div className="flex items-center gap-1.5 min-w-0">
                <Building2 className="h-4 w-4 shrink-0" />
                <span className="font-medium truncate">{currentTenant?.name ?? 'Select tenant'}</span>
              </div>
              {!isSingleTenant && <ChevronDown className={`h-4 w-4 shrink-0 ml-2 ${isOnTenantSelectPage ? 'text-primary-foreground' : 'text-muted-foreground'}`} />}
            </button>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent side={collapsed ? 'right' : 'top'} align="start" sideOffset={8} className={collapsed ? 'min-w-[180px]' : 'w-[var(--radix-dropdown-menu-trigger-width)]'}>
          {activeTenants.map(tenant => {
            const isActive = tenant.id === currentTenant?.id
            return (
              <DropdownMenuItem
                key={tenant.id}
                onClick={() => handleSwitch(tenant)}
                className="flex items-center gap-2"
              >
                <Check className={`h-4 w-4 shrink-0 ${isActive ? 'opacity-100' : 'opacity-0'}`} />
                <span>{tenant.name}</span>
              </DropdownMenuItem>
            )
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
